import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, clearSessionToken } from "../lib/api";

const AuthContext = createContext(null);

/**
 * Permission propagation strategy (security-critical):
 *
 * Server stamps every authenticated API response with the X-Permissions-Version
 * header. AuthProvider tracks the version in a ref. An axios response
 * interceptor compares each response's version against the ref — if higher,
 * it fires an immediate /auth/me re-fetch so UI gating + cached `user`
 * reflect the change without any user action.
 *
 * Additional propagation triggers:
 *   - 5s heartbeat
 *   - Window focus / visibilitychange (the user came back to the tab)
 *   - 401/403 axios responses (session may have been revoked)
 *
 * Result: permission changes take effect on the very next API call the
 * affected user makes (at worst, within 5s otherwise). No stale state.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const permVersionRef = useRef(0);
  const inflightRef = useRef(false);

  const checkAuth = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      permVersionRef.current = res.data?.permissions_version || 0;
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // ───── Real-time permission propagation ─────
  useEffect(() => {
    // Axios interceptor — compares X-Permissions-Version on every response.
    const interceptorId = api.interceptors.response.use(
      (response) => {
        const headerVer = parseInt(response.headers?.["x-permissions-version"] || "0", 10);
        if (!Number.isNaN(headerVer) && headerVer > 0 && headerVer !== permVersionRef.current) {
          // Bump local ref FIRST to avoid an interceptor loop with the /auth/me refetch.
          permVersionRef.current = headerVer;
          // Only refetch if we already have an established user — fresh login flows handle it themselves.
          if (user) checkAuth();
        }
        return response;
      },
      (error) => {
        // 401/403 → session may have been disabled or stripped; force refresh.
        const status = error?.response?.status;
        if ((status === 401 || status === 403) && user) {
          // 401 means session is gone — let the next render redirect to login.
          checkAuth();
        }
        return Promise.reject(error);
      },
    );

    // Heartbeat: re-check every 5s as a safety net for users on idle tabs
    // (pilot tuning — was 60s. Server enforcement is already instant; this
    // just tightens the worst-case UI catch-up window for tabs that aren't
    // making other API calls.)
    const heartbeat = window.setInterval(() => { if (user) checkAuth(); }, 5_000);

    // Focus / visibility: re-check when the user returns to the tab.
    const onFocus = () => { if (user && document.visibilityState === "visible") checkAuth(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      api.interceptors.response.eject(interceptorId);
      window.clearInterval(heartbeat);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [checkAuth, user]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* noop */ }
    clearSessionToken();
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

/** Returns true if the current user has the given permission key.
 *  Admin always returns true — EXCEPT for `view_confidential`, which is a
 *  per-user opt-in even for admins (mirrors the server-side carve-out in
 *  /app/backend/permissions.py). Falls back to false when unauthenticated. */
export const useHasPermission = (key) => {
  const { user } = useAuth();
  if (!user) return false;
  // Confidential carve-out MUST run before the admin short-circuit.
  if (key === "view_confidential") {
    // Master admin (system owner) always has this. Every other admin is
    // denied by default — must be granted explicitly via permissions override.
    if (user.is_master_admin === true) return true;
    return !!(user.permissions_effective || {})[key];
  }
  // Phase C1 — same seal as view_confidential.
  if (key === "manage_cashflow") {
    if (user.is_master_admin === true) return true;
    return !!(user.permissions_effective || {})[key];
  }
  if (user.role === "admin") return true;
  const eff = user.permissions_effective || {};
  return !!eff[key];
};

/** Returns the accessible site list for the current user, plus an `all` flag. */
export const useAccessibleSites = () => {
  const { user } = useAuth();
  if (!user) return { sites: [], all: false };
  return { sites: user.assigned_sites || [], all: !!user.all_sites };
};
